import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { ConnectionsService, SourcedProviderConnected } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { formatEin } from '../../../../../js/utils/string';
import { Button } from '../../../components/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogCloseButton,
  DialogTitle,
} from '../../../components/dialog';
import { Description, Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { Textarea } from '../../../components/textarea';
import {
  OPPORTUNITY_QUERY_KEY,
  ORGANIZATION_OPPORTUNITIES_LIST_QUERY_KEY,
  STAFF_OPPORTUNITIES_LIST_QUERY_KEY,
} from './queryKeys';

const optionalPositiveRate = z.preprocess((raw) => {
  if (raw === '' || raw === null || raw === undefined) return undefined;
  const n = typeof raw === 'string' ? Number.parseFloat(raw) : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}, z.number().positive().optional());

const optionalPositiveInt = z.preprocess((raw) => {
  if (raw === '' || raw === null || raw === undefined) return undefined;
  const n = typeof raw === 'string' ? Number.parseInt(raw, 10) : Number(raw);
  return Number.isInteger(n) ? n : undefined;
}, z.number().int().positive().optional());

const inviteProviderFormSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    email: z.string().trim().min(1, 'Email is required').email('Enter a valid email'),
    npi: z.string().regex(/^\d{10}$/, 'NPI must be exactly 10 digits'),
    businessName: z.string().trim().min(1, 'Business name is required'),
    ein: z.string().regex(/^\d{2}-\d{7}$/, 'Enter a valid EIN (e.g. 12-3456789)'),
    rates: z.object({
      day: optionalPositiveRate,
      night: optionalPositiveRate,
      call: optionalPositiveRate,
      '24h': optionalPositiveRate,
    }),
    targetHours: optionalPositiveInt,
    message: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const hasRate = Object.values(data.rates).some((value) => value !== undefined);

    if (!hasRate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter at least one rate',
        path: ['rates'],
      });
    }
  });

type InviteProviderFormData = z.infer<typeof inviteProviderFormSchema>;

type InviteProviderPayload = {
  first_name: string;
  last_name: string;
  email: string;
  npi: string;
  business_name: string;
  ein: string;
  rates: Record<string, number>;
  opportunity_id: string;
  message?: string;
  target_hours?: number;
};

const defaultValues: InviteProviderFormData = {
  firstName: '',
  lastName: '',
  email: '',
  npi: '',
  businessName: '',
  ein: '',
  rates: {
    day: undefined,
    night: undefined,
    call: undefined,
    '24h': undefined,
  },
  targetHours: undefined,
  message: '',
};

function toInviteProviderPayload(
  data: InviteProviderFormData,
  opportunityId: string
): InviteProviderPayload {
  const rates = Object.fromEntries(
    Object.entries(data.rates).filter(([, value]) => value !== undefined)
  ) as Record<string, number>;

  return {
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email,
    npi: data.npi,
    business_name: data.businessName.trim(),
    ein: data.ein.replace(/\D/g, ''),
    rates,
    opportunity_id: opportunityId,
    message: data.message || undefined,
    target_hours: data.targetHours,
  };
}

interface InviteProviderDialogProps {
  open: boolean;
  onClose: () => void;
  opportunityId: string;
}

export function InviteProviderDialog({ open, onClose, opportunityId }: InviteProviderDialogProps) {
  const queryClient = useQueryClient();
  const [connectedProvider, setConnectedProvider] = useState<SourcedProviderConnected | null>(null);

  const form = useForm<InviteProviderFormData>({
    resolver: zodResolver(inviteProviderFormSchema),
    defaultValues,
    mode: 'onChange',
  });

  const inviteMutation = useMutation({
    mutationFn: (payload: InviteProviderPayload) =>
      ConnectionsService.connectionsSourcedProviderInviteCreate({
        requestBody: payload,
      }),
    onSuccess: (data: SourcedProviderConnected | undefined) => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATION_OPPORTUNITIES_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [STAFF_OPPORTUNITIES_LIST_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [OPPORTUNITY_QUERY_KEY, opportunityId] });
      // A 200 response (with a body) means an existing provider was matched and
      // connected; a 201 (empty body) means a new sourced provider was invited.
      if (data?.email) {
        setConnectedProvider(data);
        onClose();
        return;
      }
      toast.success('Provider Invitation Sent');
      onClose();
    },
    onError: (error: Error) => {
      handleApiError(error, {
        messagePrefix: 'Error sending invitation',
        toastOptions: { autoClose: false },
      });
    },
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open, form]);

  const onSubmit = (data: InviteProviderFormData) => {
    inviteMutation.mutate(toInviteProviderPayload(data, opportunityId));
  };

  const ratesError = form.formState.errors.rates?.message;

  return (
    <>
      <Dialog open={open} onClose={onClose} size="2xl">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
            <DialogTitle>Invite Provider</DialogTitle>
            <DialogCloseButton onClick={onClose} />
          </div>

          <DialogBody className="tw:mt-1">
            <Description>
              Use this form to invite a provider to the opportunity as a 1099 contractor. They do
              not need to be a current Sendit user. They will receive an email with a link to
              register and accept the opportunity connection.
            </Description>

            <div className="tw:space-y-4">
              <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
                <Field>
                  <Label htmlFor="invite-provider-first-name">
                    First name <span className="tw:text-stoplight-danger">*</span>
                  </Label>
                  <Input
                    id="invite-provider-first-name"
                    type="text"
                    autoComplete="off"
                    className="tw:w-full"
                    data-1p-ignore
                    {...form.register('firstName')}
                  />
                  {form.formState.errors.firstName && (
                    <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                      {form.formState.errors.firstName.message}
                    </p>
                  )}
                </Field>

                <Field>
                  <Label htmlFor="invite-provider-last-name">
                    Last name <span className="tw:text-stoplight-danger">*</span>
                  </Label>
                  <Input
                    id="invite-provider-last-name"
                    type="text"
                    autoComplete="off"
                    className="tw:w-full"
                    data-1p-ignore
                    {...form.register('lastName')}
                  />
                  {form.formState.errors.lastName && (
                    <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                      {form.formState.errors.lastName.message}
                    </p>
                  )}
                </Field>
              </div>

              <Field>
                <Label htmlFor="invite-provider-email">
                  Email <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  id="invite-provider-email"
                  type="email"
                  autoComplete="off"
                  className="tw:w-full"
                  data-1p-ignore
                  {...form.register('email')}
                />
                {form.formState.errors.email && (
                  <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </Field>

              <Field>
                <Label htmlFor="invite-provider-npi">
                  NPI <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  id="invite-provider-npi"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={10}
                  className="tw:w-full"
                  data-1p-ignore
                  {...form.register('npi')}
                />
                {form.formState.errors.npi && (
                  <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                    {form.formState.errors.npi.message}
                  </p>
                )}
              </Field>

              <Field>
                <Label htmlFor="invite-provider-business-name">
                  Business name <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Input
                  id="invite-provider-business-name"
                  type="text"
                  autoComplete="off"
                  className="tw:w-full"
                  data-1p-ignore
                  {...form.register('businessName')}
                />
                {form.formState.errors.businessName && (
                  <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                    {form.formState.errors.businessName.message}
                  </p>
                )}
              </Field>

              <Field>
                <Label htmlFor="invite-provider-ein">
                  EIN <span className="tw:text-stoplight-danger">*</span>
                </Label>
                <Controller
                  control={form.control}
                  name="ein"
                  render={({ field }) => (
                    <Input
                      id="invite-provider-ein"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="tw:w-full"
                      data-1p-ignore
                      placeholder="12-3456789"
                      value={field.value ?? ''}
                      onBlur={field.onBlur}
                      onChange={(event) => field.onChange(formatEin(event.target.value))}
                    />
                  )}
                />
                {form.formState.errors.ein && (
                  <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                    {form.formState.errors.ein.message}
                  </p>
                )}
              </Field>

              <div>
                <p className="tw:mb-2 tw:text-sm tw:font-medium tw:text-zinc-950 tw:dark:text-white">
                  Rates <span className="tw:text-stoplight-danger">*</span>
                  <span className="tw:ml-1 tw:font-normal tw:text-zinc-500 tw:dark:text-zinc-400">
                    (at least one required)
                  </span>
                  <div className="tw:italic tw:text-zinc-500 tw:text-xs tw:p-2 tw:rounded-md tw:bg-zinc-200/50">
                    The rates represent the hourly rates to be paid to the clinician by shift type
                  </div>
                </p>

                {ratesError && (
                  <p className="tw:mb-2 tw:text-sm tw:text-stoplight-danger">{ratesError}</p>
                )}

                <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
                  <Field>
                    <Label htmlFor="invite-provider-day-rate">Day rate</Label>
                    <Input
                      id="invite-provider-day-rate"
                      type="number"
                      step="any"
                      className="tw:w-full"
                      {...form.register('rates.day')}
                    />
                    {form.formState.errors.rates?.day && (
                      <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                        {form.formState.errors.rates.day.message}
                      </p>
                    )}
                  </Field>

                  <Field>
                    <Label htmlFor="invite-provider-night-rate">Night rate</Label>
                    <Input
                      id="invite-provider-night-rate"
                      type="number"
                      step="any"
                      className="tw:w-full"
                      {...form.register('rates.night')}
                    />
                    {form.formState.errors.rates?.night && (
                      <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                        {form.formState.errors.rates.night.message}
                      </p>
                    )}
                  </Field>

                  <Field>
                    <Label htmlFor="invite-provider-call-rate">Call rate</Label>
                    <Input
                      id="invite-provider-call-rate"
                      type="number"
                      step="any"
                      className="tw:w-full"
                      {...form.register('rates.call')}
                    />
                    {form.formState.errors.rates?.call && (
                      <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                        {form.formState.errors.rates.call.message}
                      </p>
                    )}
                  </Field>

                  <Field>
                    <Label htmlFor="invite-provider-24h-rate">24hr rate</Label>
                    <Input
                      id="invite-provider-24h-rate"
                      type="number"
                      step="any"
                      className="tw:w-full"
                      {...form.register('rates.24h')}
                    />
                    {form.formState.errors.rates?.['24h'] && (
                      <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                        {form.formState.errors.rates['24h'].message}
                      </p>
                    )}
                  </Field>
                </div>
              </div>

              <Field>
                <Label htmlFor="invite-provider-target-hours">Target hours</Label>
                <Input
                  id="invite-provider-target-hours"
                  type="number"
                  inputMode="numeric"
                  placeholder="Enter target hours per month"
                  min={1}
                  step={1}
                  className="tw:w-full"
                  {...form.register('targetHours')}
                />
                {form.formState.errors.targetHours && (
                  <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                    {form.formState.errors.targetHours.message}
                  </p>
                )}
              </Field>

              <Field>
                <Label htmlFor="invite-provider-message">Message</Label>
                <Textarea
                  id="invite-provider-message"
                  rows={3}
                  className="tw:w-full"
                  {...form.register('message')}
                />
              </Field>
            </div>
          </DialogBody>

          <DialogActions>
            <Button type="button" plain onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="brandgreen1"
              disabled={!form.formState.isValid || inviteMutation.isPending}
            >
              {inviteMutation.isPending ? 'Sending...' : 'Invite'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={connectedProvider !== null}
        onClose={() => setConnectedProvider(null)}
        size="md"
      >
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <DialogTitle>Provider Connected</DialogTitle>
          <DialogCloseButton onClick={() => setConnectedProvider(null)} />
        </div>
        <DialogBody className="tw:mt-1">
          <Description>
            This provider is already on Sendit, so they were connected to the opportunity directly.
            Please confirm this is the correct provider.
          </Description>
          {connectedProvider && (
            <dl className="tw:mt-4 tw:space-y-3">
              <div>
                <dt className="tw:text-sm tw:font-medium tw:text-zinc-500">Name</dt>
                <dd className="tw:text-sm tw:text-zinc-950">{connectedProvider.full_name}</dd>
              </div>
              <div>
                <dt className="tw:text-sm tw:font-medium tw:text-zinc-500">Email</dt>
                <dd className="tw:text-sm tw:text-zinc-950">{connectedProvider.email}</dd>
              </div>
              <div>
                <dt className="tw:text-sm tw:font-medium tw:text-zinc-500">NPI</dt>
                <dd className="tw:text-sm tw:text-zinc-950">{connectedProvider.npi}</dd>
              </div>
            </dl>
          )}
        </DialogBody>
        <DialogActions>
          <Button type="button" color="brandgreen1" onClick={() => setConnectedProvider(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
